import React, { useEffect, useMemo, useState } from "react";
import { getIn } from "formik";
import CustomDropdown from "../common-dropdown/CustomDropdown";
import CustomInput from "../input/CustomInput";
import {
  ADDRESS_FIELD_HALF_GRID,
  StyledAddressFieldGrid,
  StyledLocationFieldCell,
  StyledLocationFieldsRow,
} from "../../address-default/style";
import {
  DEFAULT_ADDRESS_COUNTRY,
  resolveCountryIso2,
  resolveStateIso2,
  toCityDropdownOptions,
  toCountryDropdownOptions,
  toStateDropdownOptions,
} from "../../../utils/addressLocationOptions";

/**
 * Country → State → City (city depends on selected state). Optional zip on same row as city.
 * Persists country, state, city (and zip when shown) as stored strings for the API.
 *
 * @param {string} [fieldPrefix] - `addresses[0]` or `""` for flat fields
 * @param {boolean} [showZip] - render zip beside city
 * @param {string} [zipPath] - Formik path when showZip (e.g. `addresses[0].zip_code` or `zip_code`)
 * Location fields render in an equal-width CSS grid row (3 or 4 columns).
 */
export default function CountryStateFields({
  index = 0,
  fieldPrefix,
  countryKey = "country",
  stateKey = "state",
  cityKey = "city",
  countryLabel = "Country",
  stateLabel = "State/Province",
  cityLabel = "City",
  /** When true, country is hidden and `defaultCountry` is applied (use everywhere except profile address forms). */
  hideCountry = true,
  defaultCountry = DEFAULT_ADDRESS_COUNTRY,
  countries,
  loadingCountries,
  getStatesForCountry,
  getCitiesForCountryState,
  values,
  errors,
  touched,
  setFieldValue,
  handleBlur,
  showZip = false,
  zipPath = "",
  formGrid = false,
  /** Render as sibling MUI Grid cells (e.g. lg:4 + lg:4 + lg:4) inside a Grid container */
  muiGridRow = false,
  gridCellSize,
}) {
  const resolvedGridCellSize =
    gridCellSize ??
    (showZip && zipPath
      ? ADDRESS_FIELD_HALF_GRID
      : { lg: 4, md: 4, sm: 12, xs: 12 });

  const twoRowHalfGrid = !muiGridRow && hideCountry && showZip && Boolean(zipPath);

  const pathBase =
    fieldPrefix !== undefined && fieldPrefix !== null
      ? fieldPrefix
      : `addresses[${index}]`;
  const countryPath = pathBase === "" ? countryKey : `${pathBase}.${countryKey}`;
  const statePath = pathBase === "" ? stateKey : `${pathBase}.${stateKey}`;
  const cityPath = pathBase === "" ? cityKey : `${pathBase}.${cityKey}`;

  const countryVal = getIn(values, countryPath);
  const effectiveCountryVal =
    hideCountry && defaultCountry
      ? countryVal || defaultCountry
      : countryVal;
  const stateVal = getIn(values, statePath);
  const cityVal = getIn(values, cityPath);
  const zipVal = showZip && zipPath ? getIn(values, zipPath) : undefined;

  const [stateEntities, setStateEntities] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [statesReady, setStatesReady] = useState(false);

  const [cityOptions, setCityOptions] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citiesReady, setCitiesReady] = useState(false);

  const countryOptions = useMemo(
    () => toCountryDropdownOptions(countries),
    [countries]
  );

  useEffect(() => {
    if (hideCountry && defaultCountry && !countryVal) {
      setFieldValue(countryPath, defaultCountry);
    }
  }, [
    hideCountry,
    defaultCountry,
    countryVal,
    countryPath,
    setFieldValue,
    countries.length,
  ]);

  useEffect(() => {
    if (!countries.length) {
      setStateOptions([]);
      setStateEntities([]);
      setStatesReady(false);
      return undefined;
    }
    const iso = resolveCountryIso2(countries, effectiveCountryVal);
    if (!iso) {
      setStateOptions([]);
      setStateEntities([]);
      setStatesReady(true);
      return undefined;
    }
    let cancelled = false;
    setStatesReady(false);
    setLoadingStates(true);
    getStatesForCountry(iso)
      .then((list) => {
        if (!cancelled) {
          const arr = Array.isArray(list) ? list : [];
          setStateEntities(arr);
          setStateOptions(toStateDropdownOptions(arr));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStateEntities([]);
          setStateOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingStates(false);
          setStatesReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCountryVal, countries, getStatesForCountry]);

  useEffect(() => {
    if (!countries.length) {
      setCityOptions([]);
      setCitiesReady(false);
      return undefined;
    }
    const countryIso = resolveCountryIso2(countries, effectiveCountryVal);
    const stateIso = resolveStateIso2(stateEntities, stateVal);
    if (!countryIso || !stateIso || !stateVal) {
      setCityOptions([]);
      setCitiesReady(true);
      return undefined;
    }
    let cancelled = false;
    setCitiesReady(false);
    setLoadingCities(true);
    getCitiesForCountryState(countryIso, stateIso)
      .then((list) => {
        if (!cancelled) setCityOptions(toCityDropdownOptions(list));
      })
      .catch(() => {
        if (!cancelled) setCityOptions([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCities(false);
          setCitiesReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    effectiveCountryVal,
    stateVal,
    stateEntities,
    countries,
    getCitiesForCountryState,
  ]);

  const setCountryWrapped = (name, val) => {
    setFieldValue(name, val);
    setFieldValue(statePath, "");
    setFieldValue(cityPath, "");
  };

  const setStateWrapped = (name, val) => {
    setFieldValue(name, val);
    setFieldValue(cityPath, "");
  };

  const dropdownLayoutProps = formGrid
    ? { formGrid: true }
    : { isprofile: true, profileForm: true };
  const inputLayoutProps = formGrid
    ? { formGrid: true }
    : { isprofile: "true", profileForm: true };

  const locationColumnCount =
    (hideCountry ? 0 : 1) + 2 + (showZip && zipPath ? 1 : 0);

  const countryFieldEl = hideCountry ? null : (
    <CustomDropdown
      options={countryOptions}
      {...dropdownLayoutProps}
      required
      customLabel={countryLabel}
      placeholder={loadingCountries ? "Loading countries…" : "Select country"}
      name={countryPath}
      value={countryVal}
      setFieldValue={setCountryWrapped}
      onBlur={handleBlur}
      disabled={loadingCountries || countryOptions.length === 0}
      error={getIn(touched, countryPath) && Boolean(getIn(errors, countryPath))}
      helperText={getIn(touched, countryPath) && getIn(errors, countryPath)}
    />
  );

  const stateHasError = getIn(touched, statePath) && Boolean(getIn(errors, statePath));
  const stateFieldEl =
    !effectiveCountryVal ? (
      <CustomInput
        {...inputLayoutProps}
        required
        customLabel={stateLabel}
        placeholder="Select country first"
        name={statePath}
        value={stateVal || ""}
        onChange={(e) => setFieldValue(statePath, e.target.value)}
        onBlur={handleBlur}
        disabled
        error={stateHasError}
        helperText={getIn(touched, statePath) && getIn(errors, statePath)}
        numberNotAllow={true}
        {...(stateHasError && {
          sx: {
            "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
              borderColor: "#d32f2f",
            },
          },
        })}
      />
    ) : !statesReady || loadingStates ? (
      <CustomDropdown
        options={[]}
        {...dropdownLayoutProps}
        required
        customLabel={stateLabel}
        placeholder="Loading states…"
        name={statePath}
        value={null}
        setFieldValue={() => {}}
        onBlur={handleBlur}
        disabled
        error={false}
        helperText=""
      />
    ) : stateOptions.length > 0 ? (
      <CustomDropdown
        options={stateOptions}
        {...dropdownLayoutProps}
        required
        customLabel={stateLabel}
        placeholder="Select state"
        name={statePath}
        value={stateVal}
        setFieldValue={setStateWrapped}
        onBlur={handleBlur}
        error={getIn(touched, statePath) && Boolean(getIn(errors, statePath))}
        helperText={getIn(touched, statePath) && getIn(errors, statePath)}
      />
    ) : (
      <CustomInput
        {...inputLayoutProps}
        required
        customLabel={stateLabel}
        placeholder="Enter state or province"
        name={statePath}
        value={stateVal || ""}
        onChange={(e) => {
          setFieldValue(statePath, e.target.value);
          setFieldValue(cityPath, "");
        }}
        onBlur={handleBlur}
        error={getIn(touched, statePath) && Boolean(getIn(errors, statePath))}
        helperText={getIn(touched, statePath) && getIn(errors, statePath)}
        numberNotAllow={true}
      />
    );

  const cityHasError = getIn(touched, cityPath) && Boolean(getIn(errors, cityPath));
  const cityFieldEl = !stateVal ? (
    <CustomInput
      {...inputLayoutProps}
      required
      customLabel={cityLabel}
      placeholder="Select state first"
      name={cityPath}
      value={cityVal || ""}
      onChange={(e) => setFieldValue(cityPath, e.target.value)}
      onBlur={handleBlur}
      disabled
      error={cityHasError}
      helperText={getIn(touched, cityPath) && getIn(errors, cityPath)}
      numberNotAllow={true}
      {...(cityHasError && {
        sx: {
          "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d32f2f",
          },
        },
      })}
    />
  ) : !citiesReady || loadingCities ? (
    <CustomDropdown
      options={[]}
      {...dropdownLayoutProps}
      required
      customLabel={cityLabel}
      placeholder="Loading cities…"
      name={cityPath}
      value={null}
      setFieldValue={() => {}}
      onBlur={handleBlur}
      disabled
      error={false}
      helperText=""
    />
  ) : cityOptions.length > 0 ? (
    <CustomDropdown
      options={cityOptions}
      {...dropdownLayoutProps}
      required
      customLabel={cityLabel}
      placeholder="Select city"
      name={cityPath}
      value={cityVal}
      setFieldValue={setFieldValue}
      onBlur={handleBlur}
      error={getIn(touched, cityPath) && Boolean(getIn(errors, cityPath))}
      helperText={getIn(touched, cityPath) && getIn(errors, cityPath)}
    />
  ) : (
    <CustomInput
      {...inputLayoutProps}
      required
      customLabel={cityLabel}
      placeholder="Enter city"
      name={cityPath}
      value={cityVal || ""}
      onChange={(e) => setFieldValue(cityPath, e.target.value)}
      onBlur={handleBlur}
      error={getIn(touched, cityPath) && Boolean(getIn(errors, cityPath))}
      helperText={getIn(touched, cityPath) && getIn(errors, cityPath)}
      numberNotAllow={true}
    />
  );

  const zipFieldEl =
    showZip && zipPath ? (
      <CustomInput
        required
        type="text"
        inputProps={{ maxLength: 6 }}
        customLabel="Zip/Postal Code"
        name={zipPath}
        value={zipVal ?? ""}
        onChange={(e) => {
          const val = e.target.value.slice(0, 6);
          setFieldValue(zipPath, val);
        }}
        onBlur={handleBlur}
        error={getIn(touched, zipPath) && Boolean(getIn(errors, zipPath))}
        helperText={getIn(touched, zipPath) && getIn(errors, zipPath)}
        {...inputLayoutProps}
      />
    ) : null;

  if (muiGridRow) {
    return (
      <>
        {!hideCountry && countryFieldEl ? (
          <StyledAddressFieldGrid size={resolvedGridCellSize}>
            {countryFieldEl}
          </StyledAddressFieldGrid>
        ) : null}
        <StyledAddressFieldGrid size={resolvedGridCellSize}>{stateFieldEl}</StyledAddressFieldGrid>
        <StyledAddressFieldGrid size={resolvedGridCellSize}>{cityFieldEl}</StyledAddressFieldGrid>
        {showZip && zipPath ? (
          <StyledAddressFieldGrid size={resolvedGridCellSize}>{zipFieldEl}</StyledAddressFieldGrid>
        ) : null}
      </>
    );
  }

  return (
    <StyledAddressFieldGrid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <StyledLocationFieldsRow
        $columns={locationColumnCount}
        $formGrid={formGrid}
        $twoRowHalfGrid={twoRowHalfGrid}
      >
        {!hideCountry ? (
          <StyledLocationFieldCell>{countryFieldEl}</StyledLocationFieldCell>
        ) : null}
        <StyledLocationFieldCell>{stateFieldEl}</StyledLocationFieldCell>
        <StyledLocationFieldCell>{cityFieldEl}</StyledLocationFieldCell>
        {showZip && zipPath ? (
          <StyledLocationFieldCell>{zipFieldEl}</StyledLocationFieldCell>
        ) : null}
      </StyledLocationFieldsRow>
    </StyledAddressFieldGrid>
  );
}
